import { RefObject, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DownloadOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import html2canvas from 'html2canvas';
import { jsPDF } from 'jspdf';
import { Button } from '@/ui';
import { pickThemeFontStyles } from '@/theme';
import {
  PDF_BLOCK_GAP_MM,
  PDF_MARGIN_MM,
  PackedSlice,
  Segment,
  TableChunk,
  TableMeasure,
  mergeVerticalRanges,
  packAtomicSegments,
  packTable,
  toContiguousSegments,
} from './pdfLayout';
import { preparePdfClone } from './preparePdfClone';

interface IExportPdfButtonProps {
  disabled?: boolean;
  targetRef: RefObject<HTMLElement>;
  fileName?: string;
  onExportStateChange?: (exporting: boolean) => void;
}

const StyledExportButton = styled(Button)`
  &&&.template-button {
    ${pickThemeFontStyles('14', '20', 'SEMI_NORMAL')};
    padding: 7px 1rem;
    height: auto;

    .anticon {
      font-size: 13px;
    }
  }
`;

const wait = (ms: number) =>
  new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
  });

const unlockOverflow = (start: HTMLElement) => {
  const touched: Array<{ element: HTMLElement; value: string }> = [];
  let current: HTMLElement | null = start.parentElement;

  while (current) {
    const computed = window.getComputedStyle(current);
    if (computed.overflowX !== 'visible' || computed.overflowY !== 'visible') {
      touched.push({ element: current, value: current.style.overflow });
      current.style.overflow = 'visible';
    }
    current = current.parentElement;
  }

  return () => {
    touched.forEach(({ element, value }) => {
      element.style.overflow = value;
    });
  };
};

const captureBlock = (element: HTMLElement) => {
  const rect = element.getBoundingClientRect();
  const width = Math.ceil(rect.width);
  const height = Math.ceil(rect.height);

  return html2canvas(element, {
    scale: 2,
    useCORS: true,
    logging: false,
    backgroundColor: '#ffffff',
    scrollX: 0,
    scrollY: -window.scrollY,
    windowWidth: Math.max(width, document.documentElement.clientWidth),
    onclone: (doc, clonedNode) => {
      doc.querySelectorAll('[data-pdf-export-cover]').forEach((coverNode) => {
        coverNode.remove();
      });
      preparePdfClone(doc, clonedNode, width, height);
    },
  });
};

const cropCanvas = (source: HTMLCanvasElement, topPx: number, bottomPx: number, scaleY: number) => {
  const start = Math.max(0, Math.floor(topPx * scaleY));
  const end = Math.min(source.height, Math.ceil(bottomPx * scaleY));
  const height = Math.max(1, end - start);
  const canvas = document.createElement('canvas');
  canvas.width = source.width;
  canvas.height = height;
  const context = canvas.getContext('2d');
  if (!context) return canvas;
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  context.drawImage(source, 0, start, source.width, height, 0, 0, source.width, height);
  return canvas;
};

const stitchCanvases = (parts: HTMLCanvasElement[]) => {
  const width = parts[0]?.width ?? 1;
  const height = parts.reduce((sum, part) => sum + part.height, 0);
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = Math.max(1, height);
  const context = canvas.getContext('2d');
  if (!context) return canvas;
  context.fillStyle = '#ffffff';
  context.fillRect(0, 0, canvas.width, canvas.height);
  let offset = 0;
  parts.forEach((part) => {
    context.drawImage(part, 0, offset);
    offset += part.height;
  });
  return canvas;
};

const measureTable = (block: HTMLElement): TableMeasure | null => {
  const head = block.querySelector('thead');
  if (!head) return null;

  const blockRect = block.getBoundingClientRect();
  const headRect = head.getBoundingClientRect();
  const rows = Array.from(block.querySelectorAll('tbody tr'))
    .filter((row) => {
      if (row.classList.contains('ant-table-measure-row')) return false;
      if (row.getAttribute('aria-hidden') === 'true' && row.getBoundingClientRect().height < 1) return false;
      return row.getBoundingClientRect().height > 1;
    })
    .map((row) => {
      const rect = row.getBoundingClientRect();
      return { top: rect.top - blockRect.top, bottom: rect.bottom - blockRect.top };
    });

  return {
    cardHeight: blockRect.height,
    headTop: headRect.top - blockRect.top,
    headBottom: headRect.bottom - blockRect.top,
    rows,
  };
};

const buildSegments = (block: HTMLElement): Segment[] => {
  const blockRect = block.getBoundingClientRect();
  const nodes = Array.from(block.querySelectorAll<HTMLElement>('[data-pdf-atomic]')).filter(
    (element) => element.closest('[data-pdf-block], [data-pdf-section]') === block,
  );
  const ranges = nodes.map((element) => {
    const rect = element.getBoundingClientRect();
    return { top: rect.top - blockRect.top, bottom: rect.bottom - blockRect.top };
  });

  return toContiguousSegments(mergeVerticalRanges(ranges), blockRect.height);
};

interface IPlacedImage {
  canvas: HTMLCanvasElement;
  pageBreakBefore: boolean;
  scale: number;
}

const tableImages = (source: HTMLCanvasElement, chunks: TableChunk[], scaleY: number): IPlacedImage[] =>
  chunks.map((chunk) => {
    const body = cropCanvas(source, chunk.cropTop, chunk.cropBottom, scaleY);
    const canvas = chunk.repeatHead
      ? stitchCanvases([cropCanvas(source, chunk.headTop, chunk.headBottom, scaleY), body])
      : body;

    return { canvas, pageBreakBefore: chunk.pageBreakBefore, scale: chunk.scale };
  });

const segmentImages = (source: HTMLCanvasElement, slices: PackedSlice[], scaleY: number): IPlacedImage[] =>
  slices.map((slice) => ({
    canvas: cropCanvas(source, slice.top, slice.bottom, scaleY),
    pageBreakBefore: slice.pageBreakBefore,
    scale: slice.scale,
  }));

const ExportPdfButton = ({
  disabled,
  targetRef,
  fileName = 'cancelled-orders.pdf',
  onExportStateChange,
}: IExportPdfButtonProps) => {
  const { t } = useTranslation(['dashboard']);
  const [isExporting, setIsExporting] = useState(false);

  const handleExport = async () => {
    const node = targetRef.current;
    if (!node || isExporting) return;

    setIsExporting(true);
    const cover = document.createElement('div');
    cover.setAttribute('data-pdf-export-cover', 'true');
    cover.style.cssText =
      'position:fixed;inset:0;z-index:2147483647;background:rgba(255,255,255,0.92);pointer-events:auto;';
    document.body.appendChild(cover);

    onExportStateChange?.(true);
    node.setAttribute('data-pdf-exporting', 'true');
    const restoreOverflow = unlockOverflow(node);

    try {
      await wait(100);
      await new Promise<void>((resolve) => {
        window.requestAnimationFrame(() => resolve());
      });
      await wait(400);

      const blocks = Array.from(node.querySelectorAll<HTMLElement>('[data-pdf-block]')).sort((left, right) => {
        const leftOrder = Number(left.getAttribute('data-pdf-order') ?? 0);
        const rightOrder = Number(right.getAttribute('data-pdf-order') ?? 0);
        return leftOrder - rightOrder;
      });
      const targets = blocks.length > 0 ? blocks : [node];

      const pdf = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'a4' });
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const usableWidth = pageWidth - PDF_MARGIN_MM * 2;
      const usableHeight = pageHeight - PDF_MARGIN_MM * 2;
      let cursorY = PDF_MARGIN_MM;

      const place = (image: IPlacedImage, gapAfter: boolean) => {
        const drawWidth = usableWidth;
        const drawHeight = (image.canvas.height * usableWidth) / image.canvas.width;
        const needsBreak =
          (image.pageBreakBefore && cursorY > PDF_MARGIN_MM + 0.5) ||
          (cursorY > PDF_MARGIN_MM + 0.5 && cursorY + drawHeight > pageHeight - PDF_MARGIN_MM + 1);

        if (needsBreak) {
          pdf.addPage();
          cursorY = PDF_MARGIN_MM;
        }

        pdf.addImage(image.canvas.toDataURL('image/png'), 'PNG', PDF_MARGIN_MM, cursorY, drawWidth, drawHeight);
        cursorY += drawHeight;
        if (!gapAfter) return;
        if (cursorY + PDF_BLOCK_GAP_MM <= pageHeight - PDF_MARGIN_MM) cursorY += PDF_BLOCK_GAP_MM;
        else cursorY = pageHeight - PDF_MARGIN_MM;
      };

      for (let index = 0; index < targets.length; index += 1) {
        const block = targets[index];
        const rect = block.getBoundingClientRect();
        if (rect.height < 2 || rect.width < 2) continue;

        const canvas = await captureBlock(block);
        const cssWidth = rect.width;
        const scaleY = canvas.height / rect.height;
        const pageHeightPx = (usableHeight / usableWidth) * cssWidth;
        const remainingPx = Math.max(0, ((pageHeight - PDF_MARGIN_MM - cursorY) / usableWidth) * cssWidth);
        const table = measureTable(block);
        const images = table
          ? tableImages(canvas, packTable(table, pageHeightPx, remainingPx), scaleY)
          : segmentImages(canvas, packAtomicSegments(buildSegments(block), pageHeightPx, remainingPx), scaleY);

        images.forEach((image, imageIndex) => {
          place(image, imageIndex === images.length - 1);
        });
      }

      pdf.save(fileName);
    } finally {
      restoreOverflow();
      node.removeAttribute('data-pdf-exporting');
      node.removeAttribute('data-pdf-root');
      onExportStateChange?.(false);
      cover.remove();
      setIsExporting(false);
    }
  };

  return (
    <StyledExportButton
      type="primary"
      size="small"
      icon={<DownloadOutlined />}
      onClick={handleExport}
      disabled={disabled || isExporting}
    >
      {t('cancelled_orders_export')}
    </StyledExportButton>
  );
};

export default ExportPdfButton;
